import axios, { AxiosResponse } from 'axios';
import { PMDetailReport, PMOutcomeReport } from '@components/Admin/PMOutcomes/types';
import { handleError } from './utils';

const downloadBlobFile = async (blob: Blob, filename: string): Promise<void> => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const generateSkillLevelReportFilename = (
  parts: string[],
  reportType = 'Skill Level Report',
): string => {
  const datePart = new Date().toISOString().split('T')[0];
  const partsString = parts.join(' - ');
  return `${partsString} ${reportType} ${datePart}.xlsx`;
};

const fetchAndDownloadBlob = async (
  url: string,
  filename: string,
): Promise<{ response: AxiosResponse } | ReturnType<typeof handleError>> => {
  try {
    const response = await axios({
      url,
      method: 'GET',
      responseType: 'blob',
    });

    await downloadBlobFile(response.data, filename);
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

const fetchPendingSkillLevelReport = async (url: string) => {
  try {
    const response = await axios({
      url,
      method: 'GET',
    });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export const getStudentSkillLevelReport = async (
  studentId: number,
  curriculumId: number,
  studentName?: string,
  curriculumName?: string,
) => {
  const url = `/api/reports/skills/student/${studentId}/curriculum/${curriculumId}/`;
  const curriculumFilePart = curriculumName || curriculumId.toString();
  const studentFilePart = studentName || studentId.toString();
  const filename = generateSkillLevelReportFilename([
    curriculumFilePart,
    studentFilePart,
  ]);

  return fetchAndDownloadBlob(url, filename);
};

export const getSectionSkillLevelReport = async (
  sectionId: number,
  courseId: number,
  sectionName?: string,
  courseName?: string,
) => {
  const url = `/api/reports/skills/section/${sectionId}/`;
  const courseFilePart = courseName || courseId.toString();
  const sectionFilePart = sectionName || sectionId.toString();
  const filename = generateSkillLevelReportFilename([courseFilePart, sectionFilePart]);

  return fetchAndDownloadBlob(url, filename);
};

export const getTeacherSkillLevelReport = async (
  teacherId: number,
  curriculumId: number,
) => {
  const url = `/api/reports/skills/teacher/${teacherId}/curriculum/${curriculumId}/`;
  return fetchPendingSkillLevelReport(url);
};

export const getDistrictSkillLevelReport = async (
  districtId: number,
  curriculumId: number,
) => {
  const url = `/api/reports/skills/district/${districtId}/curriculum/${curriculumId}/`;
  return fetchPendingSkillLevelReport(url);
};

export const getCourseSkillLevelReport = async (courseId: number) => {
  const url = `/api/reports/skills/course/${courseId}/`;
  return fetchPendingSkillLevelReport(url);
};

export const getCourseSkillLevelReportsBatch = async (courseIds: number[]) => {
  const url = '/api/reports/skills/course/batch/';
  try {
    const response = await axios({
      url,
      method: 'POST',
      data: { course_ids: courseIds },
    });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export const getTeacherSkillLevelReportsBatch = async (
  reports: { teacherId: number; curriculumId: number }[],
) => {
  const url = '/api/reports/skills/teacher/batch/';
  try {
    const response = await axios({
      url,
      method: 'POST',
      data: {
        reports: reports.map(({ teacherId, curriculumId }) => ({
          teacher_id: teacherId,
          curriculum_id: curriculumId,
        })),
      },
    });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export const getStudentSkillLevelReportsBatch = async (
  reports: { studentId: number; curriculumId: number }[],
) => {
  const url = '/api/reports/skills/student/batch/';
  try {
    const response = await axios({
      url,
      method: 'POST',
      data: {
        reports: reports.map(({ studentId, curriculumId }) => ({
          student_id: studentId,
          curriculum_id: curriculumId,
        })),
      },
    });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export const getDistrictPMReport = async (districtId: number) => {
  const url = `/api/reports/pm/district/${districtId}/`;
  try {
    const response = await axios({ url, method: 'GET' });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export const getTeacherPMReport = async (teacherId: number) => {
  const url = `/api/reports/pm/teacher/${teacherId}/`;
  try {
    const response = await axios({ url, method: 'GET' });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export type PMOutcomeDistrictRequired = {
  error?: string;
  districts: Array<{ id: number; name: string }>;
  errorStatus: 400;
};

export type PMOutcomeReportResult =
  | { response: AxiosResponse<PMOutcomeReport> }
  | PMOutcomeDistrictRequired
  | ReturnType<typeof handleError>;

export const getPMOutcomeReport = async (
  period?: string | null,
  compare?: string | null,
  bands?: Array<{ label: string; lower: number }> | null,
  year?: string | null,
  districtId?: number | null,
): Promise<PMOutcomeReportResult> => {
  const params = new URLSearchParams();
  if (districtId) {
    params.set('district_id', String(districtId));
  }
  if (year) {
    params.set('year', year);
  }
  if (period) {
    params.set('period', period);
  }
  if (compare) {
    params.set('compare', compare);
  }
  if (bands && bands.length) {
    params.set('bands', JSON.stringify(bands));
  }
  const query = params.toString();
  const url = `/api/reports/pm/outcome/${query ? `?${query}` : ''}`;
  try {
    const response = await axios<PMOutcomeReport>({ url, method: 'GET' });
    return { response };
  } catch (error) {
    const response = (
      error as {
        response?: {
          status?: number;
          data?: {
            error?: string;
            districts?: Array<{ id: number; name: string }>;
          };
        };
      }
    )?.response;
    if (response?.status === 400 && response.data?.districts?.length) {
      return {
        error: response.data.error,
        districts: response.data.districts,
        errorStatus: 400,
      };
    }
    return handleError(error);
  }
};

export const getPMOutcomeDetail = async (
  period: string,
  subject: string,
  view: 'blooms' | 'standards',
  year?: string | null,
  districtId?: number | null,
): Promise<
  { response: AxiosResponse<PMDetailReport> } | ReturnType<typeof handleError>
> => {
  const params = new URLSearchParams({ period, subject, view });
  if (districtId) {
    params.set('district_id', String(districtId));
  }
  if (year) {
    params.set('year', year);
  }
  const url = `/api/reports/pm/outcome/detail/?${params.toString()}`;
  try {
    const response = await axios<PMDetailReport>({ url, method: 'GET' });
    return { response };
  } catch (error) {
    return handleError(error);
  }
};

export interface ExportPMOutcomeExcelParams {
  districtId?: number | null;
  year?: string | null;
  period?: string | null;
  compare?: string | null;
  bands?: Array<{ label: string; lower: number }> | null;
  subject: string;
  view: 'summary' | 'raw' | 'blooms' | 'standards';
}

/**
 * Queue one PM Outcomes Excel workbook for the active subject + sub-tab.
 * Download URL arrives on EXAM_REPORT_CSV_COMPLETED (shared download channel
 * with exam CSV export; payload is format-agnostic).
 */
export const exportPMOutcomeExcel = async (
  params: ExportPMOutcomeExcelParams,
): Promise<{ status: 'queued'; report_name: string }> => {
  const { districtId, year, period, compare, bands, subject, view } = params;
  const body: Record<string, unknown> = { subject, view };
  if (districtId) {
    body.district_id = districtId;
  }
  if (year) {
    body.year = year;
  }
  if (period) {
    body.period = period;
  }
  if (compare) {
    body.compare = compare;
  }
  if (bands && bands.length) {
    body.bands = bands;
  }

  const response = await axios.post('/api/reports/pm/outcome/export-xlsx/', body);
  return response.data;
};
